import { AxiosPromise } from 'axios';

import { ApiService, RequestParams } from './api.service';
import { AnswerGroup } from 'app/shared/interfaces/AnswerGroup.interface';
import { localDictionaries } from 'app/shared/dictionaries/localDictionaries';

export type DictionaryEntryKey = string | number;
export type DictionaryEntryLabel = string;
export type DictionaryEntryShortLabel = string;
export type DictionaryEntrySubLabel = string;

export interface DictionaryEntry {
  key: DictionaryEntryKey;
  value: DictionaryEntryLabel;
  valueShort?: DictionaryEntryShortLabel;
  additionals?: Array<string>;
  additionalInfo?: string;
  selected?: boolean;
  group?: AnswerGroup['groupName'];
  installmentsInfo?: {
    installments: Array<string>;
    installmentLabel: string;
    oneInstallmentGainLabel: string;
    oneInstallmentGainValue: string;
    finalPriceLabel: string;
    internalInstallments: null | boolean;
    internalInstallmentsCount: null | number;
    internalInstallmentPrice: null | string;
    internalInstallmentsRrso: null | string;
  };
  subLabels?: Array<DictionaryEntrySubLabel>;
  message?: string;
  disabled?: boolean;
  carTypeDetails?: {
    bodyType: string;
    horsePower: number;
    powerKw: number;
    seatCount: number;
    totalWeight: number;
    transmissionType: string;
    transmissionTypeShortText: string;
  };
}

export type Dictionary<T extends DictionaryEntry = DictionaryEntry> = Array<T>;

const getDictionaryAfterTimeout = (
  dictionaryKey: string,
  timeout: number,
  params?: RequestParams
): AxiosPromise<Dictionary> =>
  new Promise((resolve) => {
    setTimeout(
      () =>
        resolve(ApiService.get(`dictionaries/${dictionaryKey}`, { params })),
      timeout
    );
  });
const promiseList: Array<Promise<object>> = [];

export const DictService = {
  resolveAllDict() {
    return Promise.all(promiseList).finally(() => {
      promiseList.length = 0;
    });
  },
  getDictionary(
    dictionaryKey: string,
    params?: RequestParams
  ): AxiosPromise<Dictionary> {
    const localDict = localDictionaries[dictionaryKey];

    if (localDict) {
      return Promise.resolve({
        data: localDict,
      }) as AxiosPromise<Dictionary>;
    }

    const promise = ApiService.get(`dictionaries/${dictionaryKey}`, {
      params,
    })
      // HACK: sometimes backend returns null with no reason
      .then((response) => {
        if (!response?.data) {
          return new Promise((resolve) =>
            setTimeout(
              () =>
                resolve(
                  ApiService.get(`dictionaries/${dictionaryKey}`, { params })
                ),
              100
            )
          );
        }
        return response;
      })
      .catch(() => getDictionaryAfterTimeout(dictionaryKey, 200, params))
      .catch(() => getDictionaryAfterTimeout(dictionaryKey, 400, params));
    promiseList.push(promise as AxiosPromise<Dictionary>);
    return promise as AxiosPromise<Dictionary>;
  },
};
